import React from 'react'
import { X } from '@phosphor-icons/react'
import { Input, IconButton } from '@shopflo/ui'
import { useDiscountForm } from '../../../hooks/useDiscountForm'
import { useEntitledProducts } from '../../../hooks/useEntitledProducts'
import { DiscountApplyToType, BundleEntitlement, DiscountFieldPaths } from 'src/lib/types/discountsV2'
import { AndBadge } from '../../../common/ProductIcons'
import CatalogueGroupControl from '../common/CatalogueGroupControl'
import FormErrorText from 'src/components/common/FormErrorText'
import { discountCatalogueErrors } from 'src/lib/utils/catalogueErrorHelpers'

const BundleSelectedResources = () => {
  const {
    state: { entitlement: rawEntitlement, errors },
    actions: { setFormState }
  } = useDiscountForm()

  const entitlement = rawEntitlement as BundleEntitlement
  const { entitledCatalogue, metaData, applyTo } = entitlement

  const catalogueType = applyTo === DiscountApplyToType.SPECIFIC_COLLECTIONS ? 'COLLECTION' : 'PRODUCT_AND_VARIANT'

  const {
    handleItemsSelected,
    handleRemoveGroup,
    handleRemoveIncludedItem,
    handleRemoveExcludedItem,
    handleQuantityChange,
    handleAddEmptyGroup,
    handleVariantUpdate
  } = useEntitledProducts({
    entitlement,
    setFormState,
    catalogueType
  })

  const handleBlur = (index: number) => {
    if (!entitledCatalogue[index]?.quantity || entitledCatalogue[index]?.quantity === 0) {
      handleQuantityChange(index, '1')
    }
  }

  if (entitlement.applyTo === DiscountApplyToType.ALL_PRODUCTS) {
    return null
  }

  const renderList = () => {
    const metaItems = metaData?.entitledCatalogue || []
    const displayGroups = metaItems.length > 0 ? metaItems : [[]]

    return (
      <div className='flex flex-col'>
        {displayGroups.map((group, groupIndex) => {
          return (
            <div key={groupIndex} className='rounded-xl flex flex-col '>
              <div className='flex justify-between items-center'>
                {displayGroups.length > 1 && (
                  <div className='flex flex-col gap-3 py-3 items-center'>
                    <div className='flex items-center gap-2'>
                      {groupIndex > 0 && <AndBadge />}
                      <div className='flex items-center gap-2 '>
                        {entitledCatalogue.length > 1 && (
                          <div className='flex items-center gap-2 '>
                            <div className='min-w-10 max-w-fit'>
                              <Input
                                value={entitledCatalogue[groupIndex]?.quantity || ''}
                                onChange={e => handleQuantityChange(groupIndex, e.target.value)}
                                onBlur={() => handleBlur(groupIndex)}
                                onFocus={e => e.target.select()}
                                className='min-w-10 text-center px-1'
                                style={{
                                  width: `${(entitledCatalogue[groupIndex]?.quantity?.toString().length || 0) + 2}ch`
                                }}
                                type='number'
                                dimension='sm'
                                variant='filled'
                              />
                            </div>
                            <span className='text-text-sm-regular text-zinc-500 whitespace-nowrap'>items from</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {groupIndex > 0 && (
                  <IconButton
                    onClick={() => handleRemoveGroup(groupIndex)}
                    variant='ghost'
                    size='xs'
                    IconComponent={X}
                    className='text-zinc-500'
                  />
                )}
              </div>

              <div className='flex w-full'>
                <div className='border-l-1 border-zinc-200 w-5 rounded-l-lg border-y-1 h-auto'></div>
                <div className='flex flex-col rounded-lg px-2 w-full'>
                  <CatalogueGroupControl
                    groupIndex={groupIndex}
                    includedGroups={metaItems}
                    excludedGroups={metaData?.excludedCatalogue}
                    catalogueType={catalogueType}
                    actions={{
                      onRemoveIncludedItem: handleRemoveIncludedItem,
                      onRemoveExcludedItem: handleRemoveExcludedItem,
                      onUpdateItem: handleVariantUpdate,
                      onItemsSelected: handleItemsSelected,
                      onAddEmptyGroup: handleAddEmptyGroup
                    }}
                    errorHandling={{
                      errors: discountCatalogueErrors(errors, DiscountFieldPaths.ENTITLED_CATALOGUE)
                    }}
                    config={{
                      showVariantEditor: true,
                      addText: 'Add product',
                      showExclusionOption: true,
                      emptyText: catalogueType === 'COLLECTION' ? 'Choose Collections' : 'Choose Products'
                    }}
                  />
                  <FormErrorText message={errors[`${DiscountFieldPaths.ENTITLED_CATALOGUE}`]} />
                </div>
                <div className='border-r-1 border-zinc-200 w-5 rounded-r-lg border-y-1  h-auto'></div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return <div className='flex w-full flex-col'>{renderList()}</div>
}

export default BundleSelectedResources
